/**
 * **Pesanan tertahan** — daftar berbentuk TABEL (Imron, 2026-08-03).
 *
 * ### ⭐ Kenapa tabel, bukan kartu besar atau deretan chip
 * Yang dibutuhkan kasir saat memilih bukan satu tanda melainkan **empat hal sekaligus**: tandanya,
 * berapa item, totalnya, dan sudah berapa lama. Kartu besar memuat itu tapi hanya 6–8 yang muat di
 * layar; chip cuma memuat tandanya, sehingga kasir tetap harus membuka satu per satu.
 *
 * ⛔ Baris yang sedang dipegang terminal lain ditandai dan **tak bisa diketuk**. Server memang
 * menolaknya (kunci sesi-sunting), tapi penolakan yang terjadi di depan antrean adalah penolakan
 * yang seharusnya tak perlu terjadi.
 */
import { useTranslation } from 'react-i18next';

import type { ParkedOrder } from '../api/client';

export interface ParkedListProps {
  readonly rows: readonly ParkedOrder[];
  readonly formatPrice: (canonical: string) => string;
  readonly onResume: (o: ParkedOrder) => void;
  readonly onClose: () => void;
}

/** Menit sejak ditahan — yang menolong kasir adalah **berapa lama**, bukan pukul berapa. */
function menitSejak(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
}

export function ParkedList(p: ParkedListProps) {
  const { t } = useTranslation();

  return (
    <div className="parked" data-testid="parked-list">
      <div className="parked-head">
        <h2>{t('cashier.parked.title', { n: p.rows.length })}</h2>
        <button type="button" data-testid="parked-close" onClick={p.onClose}>
          {t('cashier.parked.close')}
        </button>
      </div>

      {p.rows.length === 0 ? (
        <p className="parked-empty" data-testid="parked-empty">{t('cashier.parked.empty')}</p>
      ) : (
        <table className="parked-table">
          <thead>
            <tr>
              <th>{t('cashier.parked.tag')}</th>
              <th>{t('cashier.parked.items')}</th>
              <th>{t('cashier.parked.total')}</th>
              <th>{t('cashier.parked.age')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {p.rows.map((o) => {
              const terkunci = o.heldByName !== null;
              return (
                <tr
                  key={o.orderId}
                  className={terkunci ? 'parked-row locked' : 'parked-row'}
                  data-testid={`parked-${o.label}`}
                  data-locked={String(terkunci)}
                >
                  <td>
                    <b>{o.label}</b>{' '}
                    <span className="parked-kind">{t(`cashier.parked.kind.${o.kind}`)}</span>
                  </td>
                  <td>{o.lineCount}</td>
                  <td>{p.formatPrice(o.runningTotal)}</td>
                  <td>
                    {menitSejak(o.parkedAt) < 1
                      ? t('cashier.parked.justNow')
                      : t('cashier.parked.minutes', { n: menitSejak(o.parkedAt) })}
                  </td>
                  <td>
                    {terkunci ? (
                      /* ⚠️ Sebabnya disebut, bukan tombol yang mati tanpa penjelasan. */
                      <span className="parked-locked" data-testid={`parked-locked-${o.label}`}>
                        {t('cashier.parked.heldBy', { name: o.heldByName ?? '' })}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="action-primary"
                        data-testid={`parked-resume-${o.label}`}
                        onClick={() => { p.onResume(o); }}
                      >
                        {t('cashier.parked.resume')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
